import React, { useState, useEffect } from 'react';
import { newsletterService } from '../../services/api';
import HtmlEditor from './HtmlEditor';
import { 
  Plus, Search, Send, Trash2, Mail, Users, CheckCircle, 
  Clock, X, Save, Loader2, ChevronRight, Eye, AlertCircle,
  Filter, Calendar, RefreshCw
} from 'lucide-react';

function NewsletterManagement() {
  const [newsletters, setNewsletters] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [totalSubscribers, setTotalSubscribers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingNewsletter, setEditingNewsletter] = useState(null);
  const [activeView, setActiveView] = useState('newsletters'); // newsletters, subscribers
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    subject: '',
    content: '',
    status: 'draft',
    scheduled_at: '',
    notes: ''
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (activeView === 'subscribers') {
      loadSubscribers();
    } else {
      loadNewsletters();
    }
  }, [activeView]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [nData, sData] = await Promise.all([
        newsletterService.index(),
        newsletterService.subscribers(1)
      ]);
      setNewsletters(Array.isArray(nData) ? nData : nData?.data || []);
      setSubscribers(sData.data || []);
      setTotalSubscribers(sData.total || sData.data?.length || 0);
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadNewsletters = async () => {
    try {
      const data = await newsletterService.index();
      setNewsletters(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error('Error loading newsletters:', error);
    }
  };

  const loadSubscribers = async () => {
    try {
      const res = await newsletterService.subscribers();
      setSubscribers(res.data || []);
      setTotalSubscribers(res.total || res.data?.length || 0);
    } catch (error) {
      console.error('Error loading subscribers:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingNewsletter) {
        await newsletterService.update(editingNewsletter.id, formData);
      } else {
        await newsletterService.store(formData);
      }
      setShowModal(false);
      resetForm();
      loadNewsletters();
    } catch (error) {
      console.error('Error saving newsletter:', error);
      alert('Error saving newsletter. Check console.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSend = async (id) => {
    if (window.confirm('Are you sure you want to send this newsletter to ALL active subscribers? This action cannot be undone.')) {
      setSubmitting(true);
      try {
        await newsletterService.send(id);
        loadNewsletters();
        alert('Newsletter sent successfully!');
      } catch (error) {
        console.error('Error sending newsletter:', error);
        alert('Failed to send newsletter.');
      } finally {
        setSubmitting(false);
      }
    }
  };

  const resetForm = () => {
    setEditingNewsletter(null);
    setFormData({ subject: '', content: '', status: 'draft', scheduled_at: '', notes: '' });
  };

  const handleEdit = (n) => {
    setEditingNewsletter(n);
    setFormData({
      subject: n.subject || '',
      content: n.content || '',
      status: n.status || 'draft',
      scheduled_at: n.scheduled_at || '',
      notes: n.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this newsletter?')) {
      try {
        await newsletterService.destroy(id);
        loadNewsletters();
      } catch (error) {
        console.error('Error deleting newsletter:', error);
      }
    }
  };

  const handleDeleteSubscriber = async (id) => {
    if (window.confirm('Remove this subscriber?')) {
      try {
        await newsletterService.deleteSubscriber(id);
        loadSubscribers();
      } catch (error) {
        console.error('Error deleting subscriber:', error);
      }
    }
  };

  const stats = {
    totalNewsletters: newsletters.length,
    sent: newsletters.filter(n => n.status === 'sent').length,
    subscribersCount: totalSubscribers,
  };

  if (loading && newsletters.length === 0 && subscribers.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-100">Newsletter Management</h2>
          <p className="text-slate-400 text-sm">Design, schedule, and send professional newsletters to your subscribers.</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => { loadInitialData(); }}
            className="p-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl transition border border-slate-700"
            title="Refresh Data"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => setActiveView(activeView === 'newsletters' ? 'subscribers' : 'newsletters')}
            className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-200 px-4 py-2.5 rounded-xl transition font-medium border border-slate-700"
          >
            {activeView === 'newsletters' ? <Users size={18} /> : <Mail size={18} />}
            {activeView === 'newsletters' ? 'View Subscribers' : 'View Campaigns'}
          </button>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-xl transition font-medium shadow-lg shadow-red-900/20"
          >
            <Plus size={18} /> Create Campaign
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-slate-900/50 border border-slate-800 p-4 rounded-xl">
          <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Campaigns</p>
          <p className="text-2xl font-bold text-slate-100">{stats.totalNewsletters}</p>
        </div>
        <div className="bg-slate-900/50 border border-slate-800 p-4 rounded-xl">
          <p className="text-red-500/80 text-xs font-semibold uppercase tracking-wider">Sent</p>
          <p className="text-2xl font-bold text-slate-100">{stats.sent}</p>
        </div>
        <div className="bg-slate-900/50 border border-slate-800 p-4 rounded-xl">
          <p className="text-blue-500/80 text-xs font-semibold uppercase tracking-wider">Subscribers</p>
          <p className="text-2xl font-bold text-slate-100">{stats.subscribersCount}</p>
        </div>
        <div className="bg-slate-900/50 border border-slate-800 p-4 rounded-xl">
          <p className="text-amber-500/80 text-xs font-semibold uppercase tracking-wider">Active Rate</p>
          <p className="text-2xl font-bold text-slate-100">100%</p>
        </div>
      </div>

      {activeView === 'newsletters' ? (
        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-950/50 border-b border-slate-800">
                <tr>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Campaign Subject</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Recipients</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Sent At</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {newsletters.map((n) => (
                  <tr key={n.id} className="hover:bg-slate-800/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-slate-800 rounded-lg text-slate-400">
                          <Mail size={18} />
                        </div>
                        <p className="text-sm font-semibold text-slate-100 truncate max-w-xs">{n.subject}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        n.status === 'sent' ? 'bg-red-500/10 text-red-400 border border-red-500/20' :
                        n.status === 'scheduled' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' :
                        'bg-slate-500/10 text-slate-400 border border-slate-500/20'
                      }`}>
                        {n.status === 'sent' ? <CheckCircle size={10} /> : 
                         n.status === 'scheduled' ? <Clock size={10} /> : <AlertCircle size={10} />}
                        <span className="capitalize">{n.status}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-400">
                      {n.recipients_count || 0}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-500">
                      {n.sent_at ? new Date(n.sent_at).toLocaleString() : '-'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {n.status !== 'sent' && (
                          <button
                            onClick={() => handleSend(n.id)}
                            className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg transition"
                            title="Send Campaign Now"
                            disabled={submitting}
                          >
                            <Send size={18} />
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(n)}
                          className="p-2 text-blue-400 hover:bg-blue-400/10 rounded-lg transition"
                          title="Edit Campaign"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(n.id)}
                          className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg transition"
                          title="Delete Campaign"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {newsletters.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                      No newsletters created yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* Subscribers View */
        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-950/50 border-b border-slate-800">
                <tr>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Subscriber Email</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">Subscribed At</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {subscribers.map((s) => (
                  <tr key={s.id} className="hover:bg-slate-800/30 transition-colors">
                    <td className="px-6 py-4 text-sm text-slate-100 font-medium">
                      {s.email}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-400">
                      {s.name || '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        s.is_active ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'
                      }`}>
                        {s.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-500">
                      {new Date(s.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleDeleteSubscriber(s.id)}
                        className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg transition"
                        title="Remove Subscriber"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Campaign Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" onClick={() => !submitting && setShowModal(false)} />
          <div className="relative bg-slate-900 border border-slate-800 w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl flex flex-col shadow-2xl">
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
              <h3 className="text-xl font-bold text-slate-100">{editingNewsletter ? 'Campaign Preview' : 'Create Newsletter Campaign'}</h3>
              <button onClick={() => !submitting && setShowModal(false)} className="p-2 text-slate-500 hover:text-slate-300 transition"><X size={20} /></button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-1.5">Subject Line</label>
                    <input
                      type="text"
                      required
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="e.g. Exclusive Ortus F&B Summer Update"
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-slate-100 focus:outline-none focus:border-red-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-1.5">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-slate-100 focus:outline-none focus:border-red-500 transition-colors"
                    >
                      <option value="draft">Draft</option>
                      <option value="scheduled">Scheduled</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-1.5">Internal Notes</label>
                    <textarea
                      rows={4}
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Notes for the team..."
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-slate-100 focus:outline-none focus:border-red-500 transition-colors resize-none"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-1.5 flex items-center justify-between">
                  <span>Newsletter Content</span>
                  <span className="text-[10px] text-slate-500">HTML Supported via Editor</span>
                </label>
                <HtmlEditor 
                  value={formData.content} 
                  onChange={(val) => setFormData({ ...formData, content: val })}
                  editorKey={editingNewsletter ? `news-${editingNewsletter.id}` : 'new-news'}
                />
              </div>
            </form>

            <div className="px-6 py-4 border-t border-slate-800 flex items-center justify-end gap-3 bg-slate-900/50">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-6 py-2.5 rounded-xl border border-slate-800 text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 transition font-medium"
              >
                Discard
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || !formData.subject || !formData.content}
                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-2.5 rounded-xl transition font-bold shadow-lg shadow-red-900/20 disabled:opacity-50"
              >
                {submitting ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                {editingNewsletter ? 'Update Campaign' : 'Save Campaign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default NewsletterManagement;

